import { defineStore } from "pinia";
import axios from "axios";
import { API_URL } from "@/config/api";
import { withShioriAccessContext } from "@/api/shiori-access-context.js";
import {
  createGuestMember,
  deleteGuestMember,
  fetchGuestMembers,
  isGuestMode,
  updateGuestMember,
} from "@/utils/guest-session";

const memberRequests = new Map();

export const invalidateShioriMemberRequests = () => {
  memberRequests.clear();
};

export const useShioriMemberStore = defineStore("shioriMember", {
  state: () => ({
    members: {}, // しおりIDをキーとしたオブジェクトに変更
    friendCandidates: [],
    isLoading: false,
    error: null,
  }),
  actions: {
    async fetchFriendCandidates(query = "") {
      this.isLoading = true;
      this.error = null;
      try {
        if (isGuestMode()) {
          this.friendCandidates = [];
          return this.friendCandidates;
        }

        const response = await axios.get(`${API_URL}/users/friends`, {
          params: query ? { q: query } : {},
        });
        this.friendCandidates = response.data.map((friend) => ({
          ...friend,
          PROFILE_IMAGE_BASE64:
            friend.HAS_PROFILE_IMAGE && friend.APP_USER_CODE
              ? `${API_URL}/users/${friend.APP_USER_CODE}/profile-image`
              : null,
        }));
        return this.friendCandidates;
      } catch (err) {
        this.error = err;
        console.error("友達候補の取得エラー:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },
    async fetchMembers(shioriId, { force = false } = {}) {
      if (isGuestMode()) {
        const members = fetchGuestMembers(shioriId);
        this.members[shioriId] = members;
        this.error = null;
        return members;
      }

      if (!force && Array.isArray(this.members[shioriId])) {
        return this.members[shioriId];
      }

      if (!force && memberRequests.has(shioriId)) {
        return memberRequests.get(shioriId);
      }

      this.isLoading = true;
      this.error = null;

      const request = axios
        .get(`${API_URL}/shiori/${shioriId}/members`)
        .then((response) => {
          response.data.forEach((member) => {
            member.PROFILE_IMAGE_BASE64 =
              member.HAS_PROFILE_IMAGE && member.APP_USER_CODE
                ? `${API_URL}/users/${member.APP_USER_CODE}/profile-image`
                : null;
          });
          // しおりIDをキーとして保存
          this.members[shioriId] = response.data;
          return response.data;
        })
        .catch((err) => {
          this.error = err;
          console.error("メンバー取得エラー:", err);
          throw err;
        })
        .finally(() => {
          this.isLoading = false;
          memberRequests.delete(shioriId);
        });

      memberRequests.set(shioriId, request);
      return request;
    },
    async createMember(shioriId, memberData) {
      this.isLoading = true;
      this.error = null;
      try {
        if (isGuestMode()) {
          const member = createGuestMember(shioriId, memberData);
          this.members[shioriId] = fetchGuestMembers(shioriId);
          return member;
        }

        const response = await axios.post(
          `${API_URL}/shiori/${shioriId}/members`,
          memberData,
          withShioriAccessContext(shioriId),
        );
        // 作成成功後、リストを再取得
        await this.fetchMembers(shioriId, { force: true });
        return response.data;
      } catch (err) {
        this.error = err;
        console.error("メンバー作成エラー:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },
    async updateMember(shioriId, memberCode, memberData) {
      this.isLoading = true;
      this.error = null;
      try {
        if (isGuestMode()) {
          const member = updateGuestMember(shioriId, memberCode, memberData);
          this.members[shioriId] = fetchGuestMembers(shioriId);
          return member;
        }

        const response = await axios.put(
          `${API_URL}/shiori/${shioriId}/members/${memberCode}`,
          memberData,
          withShioriAccessContext(shioriId),
        );
        // 更新成功後、リストを再取得
        await this.fetchMembers(shioriId, { force: true });
        return response.data;
      } catch (err) {
        this.error = err;
        console.error("メンバー更新エラー:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },
    async deleteMember(shioriId, memberCode) {
      this.isLoading = true;
      this.error = null;
      try {
        if (isGuestMode()) {
          const result = deleteGuestMember(shioriId, memberCode);
          this.members[shioriId] = fetchGuestMembers(shioriId);
          return result;
        }

        await axios.delete(
          `${API_URL}/shiori/${shioriId}/members/${memberCode}`,
          withShioriAccessContext(shioriId),
        );
        // 削除成功後、リストを再取得
        await this.fetchMembers(shioriId, { force: true });
      } catch (err) {
        this.error = err;
        console.error("メンバー削除エラー:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },
    clearMembers() {
      this.members = {};
      this.error = null;
      memberRequests.clear();
    },
    clearShioriMembers(shioriId) {
      delete this.members[shioriId];
      memberRequests.delete(shioriId);
    },
  },
});
